import React from "react";
import { Radio, RadioTower } from "lucide-react";

const companion = [
  { k: "Preset", v: "USA/Canada (Recommended)" },
  { k: "Frequency", v: "910.525 MHz" },
  { k: "Bandwidth", v: "62.5 kHz" },
  { k: "Spread Factor", v: "7" },
  { k: "Coding Rate", v: "5 (or 8 for weak signals)" },
];
const repeater = [
  { k: "Preset", v: "USA/Canada (Recommended)" },
  { k: "Frequency", v: "910.525 MHz" },
  { k: "Bandwidth", v: "62.5 kHz" },
  { k: "Spread Factor", v: "7" },
  { k: "Coding Rate", v: "8" },
  { k: "Byte Path", v: "2" },
  { k: "Zero-Hop Averts", v: "4 Hours" },
  { k: "Flood Averts", v: ">= 48 Hours" },
  { k: "set flood.max.advert", v: "32" },
  { k: "set agc.reset.interval", v: "4" },
  { k: "set multi.acks", v: "1" },
  { k: "set rxdelay", v: "3" },
  { k: "set txdelay", v: "See Below" },
  { k: "set direct.txdelay", v: "See Below"}
];

const txdelay = [
  { neighbors: "0-1", txdelay: "0.3", direct: "0.1" },
  { neighbors: "2-4", txdelay: "0.5", direct: "0.5" },
  { neighbors: "5-9", txdelay: "1.0", direct: "0.5" },
  { neighbors: "10-14", txdelay: "1.5", direct: "1.0" },
  { neighbors: ">14", txdelay: "2.0", direct: "2.0" },
]

const Card = ({ title, icon: Icon, data, testid }) => (
  <div className="bg-[#13161C] border border-[#272E3A] p-6 lg:p-8" data-testid={testid}>
    <div className="flex items-center gap-3 mb-6 pb-5 border-b border-[#272E3A]">
      <Icon className="w-6 h-6 text-[#FF5500]" strokeWidth={1.5} />
      <h3 className="font-display font-bold text-white text-xl">{title}</h3>
    </div>
    <dl className="divide-y divide-[#272E3A] font-mono">
      {data.map((r) => (
        <div key={r.k} className="py-3.5 grid grid-cols-2 gap-4">
          <dt className="text-xs uppercase tracking-widest text-[#94A3B8]">{r.k}</dt>
          <dd className="text-sm text-white text-right">{r.v}</dd>
        </div>
      ))}
    </dl>
  </div>
);

const Table = ({ title, icon: Icon, data, testid }) => (
  <div className="bg-[#13161C] border border-[#272E3A] p-6 lg:p-8" data-testid={testid}>
    <div className="flex items-center gap-3 mb-3 pb-5 border-b border-[#272E3A]">
      <Icon className="w-6 h-6 text-[#FF5500]" strokeWidth={1.5} />
      <h3 className="font-display font-bold text-white text-xl">{title}</h3>
    </div>
    <p className="text-slate-400 mt-2 mb-4">
      If you've just setup your repeater, you need to wait about a week to let it learn it's surroundings and who it's neighbors are by listening to zero-hop advertisements on the mesh. If it's been around awhile, you can use the table below as a general reference on what you should set.
    </p>
    <table>
      <thead>
        <tr>
          <th>Neighbor Count</th>
          <th>TX Delay</th>
          <th>Direct TX Delay</th>
        </tr>
      </thead>
      <tbody>
        {data.map((r) => (
          <tr>
            <td>{r.neighbors}</td>
            <td>{r.txdelay}</td>
            <td>{r.direct}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function Settings() {
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24" data-testid="settings-page">
      <div className="overline mb-3">/settings — east coast preset</div>
      <h1 className="font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl tracking-tighter mb-6">
        Recommended<br />radio settings
      </h1>
      <p className="text-lg text-slate-300 max-w-3xl leading-relaxed mb-10">
        Tune your MeshCore radio to these values so you can talk to the rest of the network. Start by selecting the{" "}
        <span className="font-mono text-[#FF5500]">USA/Canada (Recommended)</span> preset in the MeshCore app, and only
        change Coding Rate if needed. When changed, the preset will show "Custom" — this is normal.
      </p>

      <div className="grid lg:grid-cols-2 gap-4 lg:gap-6 mb-12">
        <Card title="Companion Device Settings" icon={Radio} data={companion} testid="settings-companion-card" />
        <Card title="Repeater Settings" icon={RadioTower} data={repeater} testid="settings-repeater-card" />
      </div>

      <Table title="TX Delay Settings" icon={Radio} data={txdelay} testid="settings-tx-delay" />

      <div className="bg-[#0B0E14] border border-[#272E3A] border-l-2 border-l-[#FF5500] p-6 mt-6">
        <div className="overline mb-2">Note #1</div>
        <p className="text-slate-300">
          The East Coast network is unified on the USA/Canada preset. If you cannot hear traffic, check your frequency first
          (a misconfigured device on 906 or 915 MHz is the #1 cause of "the network is broken"). Confirm the spread factor
          and bandwidth match exactly.
        </p>
      </div>

      <div className="bg-[#0B0E14] border border-[#272E3A] border-l-2 border-l-[#FF5500] p-6 mt-6">
        <div className="overline mb-2">Note #2</div>
        <p className="text-slate-300">
          Be sure to check with your local MC groups as they may have different advert intervals. These are averages of what we've seen.
        </p>
      </div>
    </div>
  );
}
